import { Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { ButtonModule } from 'primeng/button';
import { DashboardFilter, currentFinYear } from '../../services/partner-dashboard';

/**
 * Reusable period + partner filter bar shared by the Partner Dashboard (overview)
 * and the Partner Records (tables) pages. Emits a `DashboardFilter` on Apply / Reset.
 *
 * The parent owns the initial load (both default to the current financial year); this
 * bar just pre-selects that FY in the UI so the two stay in sync.
 */
@Component({
  selector: 'app-partner-filter-bar',
  standalone: true,
  imports: [CommonModule, FormsModule, SelectModule, DatePickerModule, ButtonModule],
  templateUrl: './partner-filter-bar.html',
  styleUrls: ['./partner-filter-bar.css'],
})
export class PartnerFilterBar {
  @Input() isAdmin = false;
  @Input() partnerOptions: { label: string; value: number }[] = [];
  @Output() filterChange = new EventEmitter<DashboardFilter>();

  selYear = signal<number | null>(null);
  selMonth = signal<number | null>(null);
  selFinYear = signal<number | null>(null);
  fromDate = signal<Date | null>(null);
  toDate = signal<Date | null>(null);
  selPartnerId = signal<number | null>(null);

  yearOptions: { label: string; value: number }[] = [];
  monthOptions = [
    { label: 'January', value: 1 },
    { label: 'February', value: 2 },
    { label: 'March', value: 3 },
    { label: 'April', value: 4 },
    { label: 'May', value: 5 },
    { label: 'June', value: 6 },
    { label: 'July', value: 7 },
    { label: 'August', value: 8 },
    { label: 'September', value: 9 },
    { label: 'October', value: 10 },
    { label: 'November', value: 11 },
    { label: 'December', value: 12 },
  ];
  finYearOptions: { label: string; value: number }[] = [];

  constructor() {
    const now = new Date();
    const y = now.getFullYear();
    for (let yr = 2023; yr <= y + 1; yr++) this.yearOptions.push({ label: String(yr), value: yr });

    const cur = currentFinYear();
    for (let start = cur; start >= cur - 5; start--) {
      this.finYearOptions.push({
        label: `FY ${start}-${String((start + 1) % 100).padStart(2, '0')}`,
        value: start,
      });
    }
    this.selFinYear.set(cur); // default landing period: current FY
  }

  apply() {
    this.filterChange.emit(this.build());
  }

  reset() {
    this.selYear.set(null);
    this.selMonth.set(null);
    this.selFinYear.set(null);
    this.fromDate.set(null);
    this.toDate.set(null);
    this.selPartnerId.set(null);
    this.filterChange.emit({}); // all-time
  }

  /** Build the shared (period + partner) filter; an explicit From/To range wins over year/finYear. */
  private build(): DashboardFilter {
    const f: DashboardFilter = {};
    if (this.isAdmin && this.selPartnerId()) f.partnerId = this.selPartnerId()!;

    const from = this.fromDate();
    const to = this.toDate();
    if (from) {
      f.fromDate = this.fmt(from);
      if (to) f.toDate = this.fmt(to);
    } else {
      if (this.selYear()) {
        f.year = this.selYear()!;
        if (this.selMonth()) f.month = this.selMonth()!;
      }
      if (this.selFinYear()) f.finYear = this.selFinYear()!;
    }
    return f;
  }

  private fmt(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
}
